<script setup lang="ts">
import { computed } from 'vue'
import { type PreviewIncidentReportResponse } from '@roadfootage/api-client'

import { formatIncidentDateTime, formatIncidentType } from '../utils/format'

const props = defineProps<{
  incident: PreviewIncidentReportResponse
}>()

defineEmits<{ select: [] }>()

const showThumbnail = computed(
  () => !props.incident.hasContentWarning && !!props.incident.thumbnailUrl
)
const place = computed(
  () =>
    props.incident.address?.formattedAddress || props.incident.address?.suburb || 'Location unknown'
)
const formattedDate = computed(() =>
  formatIncidentDateTime(props.incident.incidentDateTime, props.incident.incidentTimezone)
)
</script>

<template>
  <q-card
    flat
    bordered
    class="search-result cursor-pointer row no-wrap"
    role="link"
    tabindex="0"
    @click="$emit('select')"
    @keyup.enter="$emit('select')"
  >
    <div class="search-result__media bg-grey-3 relative-position">
      <q-img v-if="showThumbnail" :src="incident.thumbnailUrl!" class="fit" fit="cover">
        <template #error>
          <div class="fit flex flex-center bg-grey-3">
            <q-icon name="mdi-image-broken" size="sm" color="grey-6" />
          </div>
        </template>
      </q-img>
      <div v-else class="fit flex flex-center">
        <q-icon name="mdi-camera-off" size="sm" color="grey-6" />
      </div>
      <q-badge
        v-if="incident.hasContentWarning"
        color="negative"
        text-color="white"
        label="Content Warning"
        class="absolute-top-left q-ma-xs"
      />
    </div>

    <div class="search-result__body col q-pa-sm">
      <div class="text-overline text-primary">
        {{ formatIncidentType(incident.incidentReportType) }}
      </div>
      <div class="text-subtitle2 search-result__place" :title="place">{{ place }}</div>
      <div class="text-caption text-grey-7 row items-center no-wrap q-mt-xs">
        <q-icon name="mdi-calendar" class="q-mr-xs" />
        <span>{{ formattedDate }}</span>
      </div>
    </div>
  </q-card>
</template>

<style lang="scss" scoped>
.search-result__media {
  flex: 0 0 42%;
  max-width: 260px;
  aspect-ratio: 16 / 9;
  align-self: flex-start;
}

.search-result__body {
  min-width: 0;
}

.search-result__place {
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
